import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { Loader2, Plus, Edit, Trash2, CheckCircle, XCircle } from 'lucide-react';

const Brands = () => {
    const { t } = useTranslation();
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        slug: '',
        sheetUrl: '',
        sheetName: 'Youcan-Orders',
        active: true
    });
    const [editingId, setEditingId] = useState(null);

    useEffect(() => {
        fetchBrands();
    }, [fetchBrands]);

    const fetchBrands = useCallback(async () => {
        try {
            const res = await api.get('/brands');
            if (res.data.success) {
                setBrands(res.data.data);
            }
        } catch (error) {
            console.error(t('brands.messages.fetch_failed'), error);
        } finally {
            setLoading(false);
        }
    }, [t]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingId) {
                await api.put(`/brands/${editingId}`, formData);
            } else {
                await api.post('/brands', formData);
            }
            setIsModalOpen(false);
            setEditingId(null);
            setFormData({ name: '', slug: '', sheetUrl: '', sheetName: 'Youcan-Orders', active: true });
            fetchBrands();
        } catch (error) {
            alert(error.response?.data?.message || t('brands.messages.operation_failed'));
        }
    };

    const handleEdit = (brand) => {
        setFormData({
            name: brand.name,
            slug: brand.slug,
            sheetUrl: brand.sheetUrl,
            sheetName: brand.sheetName,
            active: brand.active
        });
        setEditingId(brand._id);
        setIsModalOpen(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm(t('brands.actions.delete_confirm'))) {
            try {
                await api.delete(`/brands/${id}`);
                fetchBrands();
            } catch (error) {
                alert(t('brands.messages.delete_failed'));
            }
        }
    };

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">{t('brands.title')}</h1>
                <button
                    onClick={() => { setIsModalOpen(true); setEditingId(null); }}
                    className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700"
                >
                    <Plus size={20} /> {t('brands.actions.add')}
                </button>
            </div>

            {loading ? (
                <div className="flex justify-center p-12">
                    <Loader2 className="animate-spin text-indigo-600" size={32} />
                </div>
            ) : (
                <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50 border-b border-gray-100">
                            <tr>
                                <th className="p-4 font-semibold text-gray-600">{t('brands.table.name')}</th>
                                <th className="p-4 font-semibold text-gray-600">{t('brands.table.slug')}</th>
                                <th className="p-4 font-semibold text-gray-600">{t('brands.table.sheet_url')}</th>
                                <th className="p-4 font-semibold text-gray-600">{t('brands.table.status')}</th>
                                <th className="p-4 font-semibold text-gray-600 text-right">{t('brands.table.actions')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {brands.map(brand => (
                                <tr key={brand._id} className="hover:bg-gray-50">
                                    <td className="p-4 font-medium">{brand.name}</td>
                                    <td className="p-4 text-gray-500">{brand.slug}</td>
                                    <td className="p-4 text-blue-600 truncate max-w-xs" title={brand.sheetUrl}>
                                        {brand.sheetUrl}
                                    </td>
                                    <td className="p-4">
                                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${brand.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                            }`}>
                                            {brand.active ? <CheckCircle size={12} /> : <XCircle size={12} />}
                                            {brand.active ? t('users.status.active') : t('users.status.disabled')}
                                        </span>
                                    </td>
                                    <td className="p-4 text-right space-x-2">
                                        <button onClick={() => handleEdit(brand)} className="text-gray-400 hover:text-indigo-600">
                                            <Edit size={18} />
                                        </button>
                                        <button onClick={() => handleDelete(brand._id)} className="text-gray-400 hover:text-red-600">
                                            <Trash2 size={18} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {isModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 w-full max-w-lg">
                        <h2 className="text-xl font-bold mb-4">{editingId ? t('brands.actions.edit') : t('brands.actions.new')}</h2>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('brands.form.name')}</label>
                                <input
                                    required
                                    className="w-full border rounded-lg p-2"
                                    value={formData.name}
                                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('brands.form.slug')}</label>
                                <input
                                    required
                                    className="w-full border rounded-lg p-2"
                                    value={formData.slug}
                                    onChange={e => setFormData({ ...formData, slug: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('brands.form.sheet_url')}</label>
                                <input
                                    required
                                    className="w-full border rounded-lg p-2"
                                    value={formData.sheetUrl}
                                    onChange={e => setFormData({ ...formData, sheetUrl: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('brands.form.sheet_name')}</label>
                                <input
                                    className="w-full border rounded-lg p-2"
                                    value={formData.sheetName}
                                    onChange={e => setFormData({ ...formData, sheetName: e.target.value })}
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={formData.active}
                                    onChange={e => setFormData({ ...formData, active: e.target.checked })}
                                />
                                <label className="text-sm font-medium text-gray-700">{t('brands.form.status_active')}</label>
                            </div>
                            <div className="flex justify-end gap-3 mt-6">
                                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg">{t('brands.actions.cancel')}</button>
                                <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">{t('brands.actions.save')}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Brands;
